import { usePeriodDragTaggingMutationKey } from "@/hooks/mutations/use-drag-period";
import { useMainStore } from "@/stores/main";
import { useIsMutating } from "@tanstack/react-query";
import { ModalFooterFull, ModalHeader } from "../common/modals";
import { useTableId } from "../mapping/hooks";
import { ModalWrapper } from "./modal-wrapper";
import { AccountingPeriod, Accumulated, TargetYear } from "@/types/enums";

const periodToKorean = (
  period: AccountingPeriod | null | undefined,
): string => {
  switch (period) {
    case AccountingPeriod.BEGIN:
      return "기초";
    case AccountingPeriod.END:
      return "기말";
    case null:
      return "구분없음";
    default:
      return "";
  }
};

const targetYearToKorean = (
  targetYear: TargetYear | null | undefined,
): string => {
  switch (targetYear) {
    case TargetYear.CURRENT:
      return "당기";
    case TargetYear.PRIOR:
      return "전기";
    case null:
      return "구분없음";
    default:
      return "";
  }
};

const accumulatedToKorean = (
  accumulated: Accumulated | null | undefined,
): string => {
  switch (accumulated) {
    case Accumulated.ACC:
      return "누적";
    case Accumulated.QUARTER_ACC:
      return "3개월";
    case null:
      return "구분없음";
    default:
      return "";
  }
};

export default function PeriodDragTaggingModal({
  cellAddresses,
  period,
  targetYear,
  accumulated,
  onConfirm,
  onCancel,
}: {
  cellAddresses?: string;
  period?: AccountingPeriod | null;
  targetYear?: TargetYear | null;
  accumulated?: Accumulated | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { data: tableId } = useTableId();
  const currentSelection = useMainStore((state) => state.selection);
  const mutationKey = usePeriodDragTaggingMutationKey(
    tableId?.tableId,
    currentSelection.current,
  );
  const isMutating = useIsMutating({ mutationKey: mutationKey });

  if (!cellAddresses) return null;

  const periodKo = periodToKorean(period);
  const targetYearKo = targetYearToKorean(targetYear);
  const accumulatedKo = accumulatedToKorean(accumulated);

  // 변경되는 속성만 표시
  const changes = [];
  if (period !== undefined) changes.push(`시점: ${periodKo}`);
  if (targetYear !== undefined) changes.push(`분기: ${targetYearKo}`);
  if (accumulated !== undefined) changes.push(`기간: ${accumulatedKo}`);

  return (
    <ModalWrapper className="flex flex-col bg-white rounded-[10px] w-[425px] p-6 h-fit gap-4">
      <ModalHeader
        title="여러 셀 기간 속성 변경"
        description={`${cellAddresses}에 아래 기간 속성을 적용하시겠습니까?`}
        className="heading-1-semibold p-0"
      />

      <div className="flex flex-col px-4 py-3 border border-neutral-100 rounded-[4px] gap-2">
        {changes.map((change, index) => (
          <span key={index} className="body-1-normal-semibold text-neutral-600">
            {change}
          </span>
        ))}
      </div>

      <div className="flex flex-col gap-2">
        <span className="caption-1-regular text-neutral-500">
          * 기간 속성은 덮어씌워집니다. <br />* 데이터 정보를 담고 있는 셀만
          적용되며 헤더 정보를 담은 셀은 제외됩니다. <br />* 같은 주석목차의
          셀들만 적용됩니다.
        </span>
      </div>

      <ModalFooterFull
        cancelLabel="취소"
        confirmLabel="확인"
        onConfirm={onConfirm}
        onCancel={onCancel}
        isLoading={isMutating > 0}
      />
    </ModalWrapper>
  );
}
